/**
 * @fileoverview HTTP Client for API communication
 * @module @monorepo/api-client/core
 *
 * Modern, type-safe HTTP client with:
 * - Automatic retry with exponential backoff
 * - Request/response interceptors
 * - CSRF protection
 * - Session management
 * - Metrics tracking
 * - Timeout handling
 *
 * @see {@link https://docs.nestjs.com/techniques/http-module}
 */

import type { ApiResponse, ErrorCode } from '@monorepo/types';

// ============================================================================
// Configuration Types
// ============================================================================

import type { HttpMethod } from '@monorepo/types';

// Re-export for convenience
export type { HttpMethod };

/**
 * HTTP Client Configuration
 */
export interface HttpClientConfig {
	/** Base URL for all requests */
	readonly baseURL: string;
	/** Request timeout in milliseconds */
	readonly timeout: number;
	/** Number of retry attempts for failed requests */
	readonly retryAttempts: number;
	/** Base delay between retries in milliseconds */
	readonly retryDelay: number;
	/** Client ID for authentication */
	readonly clientId: string;
	/** Client secret for authentication */
	readonly clientSecret: string;
	/** Enable request/response logging */
	readonly enableLogging: boolean;
	/** Enable metrics collection */
	readonly enableMetrics: boolean;
	/** Default headers for all requests */
	readonly defaultHeaders?: Record<string, string>;
	/** Skip session management */
	readonly skipSession?: boolean;
}

/**
 * Request Configuration
 * Per-request overrides for client configuration
 */
export interface RequestConfig {
	/** Request timeout override */
	readonly timeout?: number;
	/** Retry attempts override */
	readonly retries?: number;
	/** Skip authentication */
	readonly skipAuth?: boolean;
	/** Skip session management */
	readonly skipSession?: boolean;
	/** Additional headers */
	readonly headers?: Record<string, string>;
	/** Query parameters */
	readonly params?: Record<string, unknown>;
}

/**
 * Request Metrics
 * Performance tracking for requests
 */
export interface RequestMetrics {
	/** Request start time */
	startTime: number;
	/** Request end time */
	endTime?: number;
	/** Request duration in milliseconds */
	duration?: number;
	/** Number of retry attempts */
	retryCount: number;
	/** Whether request succeeded */
	success: boolean;
	/** HTTP status code */
	statusCode?: number;
	/** Error message if failed */
	error?: string;
}

/**
 * API Metrics
 * Aggregate metrics for all requests
 */
export interface ApiMetrics {
	/** Total number of requests */
	readonly totalRequests: number;
	/** Number of successful requests */
	readonly successfulRequests: number;
	/** Number of failed requests */
	readonly failedRequests: number;
	/** Average response time in milliseconds */
	readonly averageResponseTime: number;
	/** Error rate (0-1) */
	readonly errorRate: number;
	/** Timestamp of last request */
	readonly lastRequestAt?: string;
}

// ============================================================================
// Error Types
// ============================================================================

/**
 * HTTP Client Error
 * Custom error for HTTP client failures
 */
export class HttpClientError extends Error {
	constructor(
		message: string,
		public readonly statusCode: number,
		public readonly code: ErrorCode,
		public readonly response?: unknown,
		public readonly request?: unknown,
	) {
		super(message);
		this.name = 'HttpClientError';
		Object.setPrototypeOf(this, HttpClientError.prototype);
	}
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Delay execution for specified milliseconds
 */
function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Calculate exponential backoff delay with jitter
 */
function calculateBackoffDelay(attempt: number, baseDelay: number): number {
	const exponentialDelay = baseDelay * Math.pow(2, attempt);
	const jitter = Math.random() * 0.1 * exponentialDelay;
	return Math.min(exponentialDelay + jitter, 30000); // Max 30 seconds
}

/**
 * Check if error is retryable
 */
function isRetryableError(error: unknown): boolean {
	if (error instanceof HttpClientError) {
		// Retry on network errors and 5xx server errors
		return error.statusCode >= 500 || error.statusCode === 0;
	}
	return true; // Retry on network errors
}

/**
 * Log message if logging is enabled
 */
function log(message: string, enableLogging: boolean): void {
	if (enableLogging) {
		console.log(`[HttpClient] ${message}`);
	}
}

// ============================================================================
// HTTP Client Class
// ============================================================================

/**
 * HTTP Client
 * Modern, type-safe HTTP client for API communication
 *
 * @example
 * ```typescript
 * const client = new HttpClient({
 *   baseURL: 'https://api.example.com',
 *   timeout: 30000,
 *   retryAttempts: 3,
 *   retryDelay: 1000,
 *   clientId: 'frontend',
 *   clientSecret: 'secret',
 *   enableLogging: true,
 *   enableMetrics: true,
 * });
 *
 * const response = await client.get<User>('/users/123');
 * if (response.success) {
 *   console.log(response.data);
 * }
 * ```
 */
export class HttpClient {
	private readonly config: HttpClientConfig;
	private readonly metrics: ApiMetrics;
	private abortController: AbortController | null = null;
	private authService: { getAccessToken(): string | null } | null = null;
	private sessionRefreshCallback: (() => Promise<void>) | null = null;
	private isRefreshingSession = false;

	constructor(config: HttpClientConfig) {
		this.config = config;
		this.metrics = {
			totalRequests: 0,
			successfulRequests: 0,
			failedRequests: 0,
			averageResponseTime: 0,
			errorRate: 0,
		};
	}

	/**
	 * Set auth service for automatic token injection
	 */
	setAuthService(authService: { getAccessToken(): string | null } | null): void {
		this.authService = authService;
	}

	/**
	 * Set session refresh callback for automatic CSRF token refresh on 403 errors
	 * The callback should call /session/verify to get a fresh token
	 */
	setSessionRefreshCallback(callback: (() => Promise<void>) | null): void {
		this.sessionRefreshCallback = callback;
	}

	// ============================================================================
	// Core Request Method
	// ============================================================================

	/**
	 * Execute HTTP request with retry logic
	 *
	 * @template T - Response data type
	 * @param endpoint - API endpoint (relative to baseURL)
	 * @param options - Request options
	 * @returns Promise resolving to API response
	 *
	 * @throws {HttpClientError} If request fails after all retries
	 */
	async request<T>(
		endpoint: string,
		options: {
			readonly method?: HttpMethod;
			readonly data?: unknown;
			readonly config?: RequestConfig;
		} = {},
	): Promise<ApiResponse<T>> {
		const { method = 'GET', data, config: requestConfig = {} } = options;

		const finalConfig = {
			timeout: requestConfig.timeout ?? this.config.timeout,
			retries: requestConfig.retries ?? this.config.retryAttempts,
			skipAuth: requestConfig.skipAuth ?? false,
			skipSession: requestConfig.skipSession ?? this.config.skipSession ?? false,
			headers: requestConfig.headers ?? {},
			params: requestConfig.params ?? {},
		};

		const metrics: RequestMetrics = {
			startTime: Date.now(),
			retryCount: 0,
			success: false,
		};

		let lastError: Error | null = null;

		// Retry loop
		for (let attempt = 0; attempt <= finalConfig.retries; attempt++) {
			try {
				metrics.retryCount = attempt;

				if (attempt > 0) {
					const delayMs = calculateBackoffDelay(attempt - 1, this.config.retryDelay);
					log(
						`Retrying request (attempt ${attempt + 1}/${finalConfig.retries + 1}) after ${delayMs}ms delay`,
						this.config.enableLogging,
					);
					await delay(delayMs);
				}

				const response = await this.executeRequest<T>(endpoint, method, data, finalConfig, metrics);

				metrics.success = true;
				this.updateMetrics(metrics);
				return response;
			} catch (error) {
				lastError = error instanceof Error ? error : new Error(String(error));

				log(
					`❌ Request attempt ${attempt + 1} failed: ${lastError.message}`,
					this.config.enableLogging,
				);

				// Handle CSRF token errors with automatic refresh
				// This includes both "Invalid CSRF token" and "CSRF token missing"
				if (
					error instanceof HttpClientError &&
					error.statusCode === 403 &&
					(error.message.toLowerCase().includes('csrf') || error.message.toLowerCase().includes('token')) &&
					this.sessionRefreshCallback &&
					!this.isRefreshingSession &&
					attempt < finalConfig.retries
				) {
					try {
						log('🔄 CSRF token error detected, refreshing session...', this.config.enableLogging);
						this.isRefreshingSession = true;
						await this.sessionRefreshCallback();
						log('✅ Session refreshed, retrying request', this.config.enableLogging);
						this.isRefreshingSession = false;
						// Continue to next retry attempt with fresh token
						continue;
					} catch (refreshError) {
						log(`❌ Session refresh failed: ${refreshError}`, this.config.enableLogging);
						this.isRefreshingSession = false;
						// Fall through to normal error handling
					}
				}

				// Don't retry on certain errors
				if (!isRetryableError(error) || attempt === finalConfig.retries) {
					break;
				}
			}
		}

		// All retries failed
		metrics.success = false;
		this.updateMetrics(metrics);

		throw (
			lastError ??
			new HttpClientError('Request failed after all retries', 0, 'NETWORK_ERROR' as ErrorCode)
		);
	}

	// ============================================================================
	// Execute Single Request
	// ============================================================================

	private async executeRequest<T>(
		endpoint: string,
		method: HttpMethod,
		data: unknown,
		config: Required<RequestConfig> & { readonly timeout: number; readonly retries: number },
		metrics: RequestMetrics,
	): Promise<ApiResponse<T>> {
		// Create abort controller for timeout
		this.abortController = new AbortController();
		const timeoutId = setTimeout(() => {
			this.abortController?.abort();
		}, config.timeout);

		try {
			// Build URL
			const baseURL = this.config.baseURL.endsWith('/')
				? this.config.baseURL
				: this.config.baseURL + '/';
			const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
			const url = new URL(cleanEndpoint, baseURL);

			// Add query parameters to URL
			if (config.params && Object.keys(config.params).length > 0) {
				Object.entries(config.params).forEach(([key, value]) => {
					if (value !== undefined && value !== null) {
						// Handle arrays (for multi-value query params)
						if (Array.isArray(value)) {
							value.forEach((item) => {
								url.searchParams.append(key, String(item));
							});
						} else {
							url.searchParams.append(key, String(value));
						}
					}
				});
			}

			// Build headers
			const headers = await this.buildHeaders(config);

			// Build request options
			const requestOptions: RequestInit = {
				method,
				headers,
				signal: this.abortController.signal,
				credentials: 'include', // Required for session cookies to be sent/received
			};

			// Add body for non-GET requests
			if (method !== 'GET' && data !== undefined) {
				requestOptions.body = JSON.stringify(data);
			}

			log(`🚀 ${method} ${url.toString()}`, this.config.enableLogging);

			// Execute request
			const response = await fetch(url.toString(), requestOptions);

			// Record metrics
			metrics.endTime = Date.now();
			metrics.duration = metrics.endTime - metrics.startTime;
			metrics.statusCode = response.status;

			log(
				`✅ ${method} ${url.toString()} - ${response.status} (${metrics.duration}ms)`,
				this.config.enableLogging,
			);

			// Handle response
			return await this.handleResponse<T>(response, endpoint, method);
		} catch (error) {
			if (error instanceof Error && error.name === 'AbortError') {
				throw new HttpClientError(
					`Request timeout after ${config.timeout}ms`,
					0,
					'TIMEOUT' as ErrorCode,
				);
			}
			throw error;
		} finally {
			clearTimeout(timeoutId);
		}
	}

	// ============================================================================
	// Header Building
	// ============================================================================

	private async buildHeaders(
		config: Required<RequestConfig> & { readonly timeout: number; readonly retries: number },
	): Promise<Record<string, string>> {
		const headers: Record<string, string> = {
			'Content-Type': 'application/json',
			Accept: 'application/json',
			...this.config.defaultHeaders,
			...config.headers,
		};

		// Add JWT token if available
		if (!config.skipAuth && this.authService) {
			const token = this.authService.getAccessToken();
			if (token) {
				headers['Authorization'] = `Bearer ${token}`;
			}
		}

		// Add client authentication
		if (!config.skipAuth) {
			headers['X-Client-ID'] = this.config.clientId;
			headers['X-Client-Secret'] = this.config.clientSecret;
		}

		// Add session headers if available
		if (!config.skipSession) {
			const sessionHeaders = this.getSessionHeaders();
			Object.assign(headers, sessionHeaders);
		}

		return headers;
	}

	private getSessionHeaders(): Record<string, string> {
		// Check if running in browser
		if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
			return {};
		}

		// ✅ Session store saves to 'ulrich-session' as JSON object
		// Structure: { sessionId: "...", csrfToken: "...", userId: "..." }
		try {
			const sessionData = localStorage.getItem('ulrich-session');
			if (!sessionData) {
				//TODO: use a proper injected logger interface
				if (this.config.enableLogging) {
					console.debug('[HttpClient] No session data in localStorage');
				}
				return {};
			}

			const parsed = JSON.parse(sessionData);
			const sessionId = parsed?.sessionId;
			const csrfToken = parsed?.csrfToken;

			if (sessionId && csrfToken) {
				const headers: Record<string, string> = {
					'X-Session-Token': sessionId,
					'X-CSRF-Token': csrfToken,
				};

				// Note: sessionId is used for both session and tracking identification
				headers['X-Tracking-ID'] = sessionId;

				// Debug log to track token usage
				if (this.config.enableLogging) {
					console.debug('[HttpClient] Using session headers:', {
						sessionId: sessionId.substring(0, 12) + '...',
						csrfToken: csrfToken.substring(0, 15) + '...'
					});
				}

				return headers;
			}
		} catch (error) {
			// Silently fail if localStorage parsing fails
			console.debug('Failed to parse session data from localStorage', error);
		}

		return {};
	}

	// ============================================================================
	// Response Handling
	// ============================================================================

	private async handleResponse<T>(
		response: Response,
		endpoint: string,
		method: HttpMethod,
	): Promise<ApiResponse<T>> {
		let responseData: unknown;

		try {
			const contentType = response.headers.get('content-type');
			if (contentType?.includes('application/json')) {
				responseData = await response.json();
			} else {
				responseData = await response.text();
			}
		} catch (error) {
			throw new HttpClientError(
				'Failed to parse response',
				response.status,
				'PARSE_ERROR' as ErrorCode,
				undefined,
				{ endpoint, method, parseError: error },
			);
		}

		// Handle successful responses
		if (response.ok) {
			if (typeof responseData === 'object' && responseData !== null) {
				const apiResponse = responseData as Partial<ApiResponse<T>>;

				// If backend already returned a wrapped response, return it as-is
				if (apiResponse.success !== undefined && 'data' in apiResponse) {
					return responseData as ApiResponse<T>;
				}

				// Otherwise, wrap the response
				return {
					success: true,
					data: responseData as T,
					timestamp: new Date().toISOString(),
				} as ApiResponse<T>;
			}

			// Wrap non-object responses
			return {
				success: true,
				data: responseData as T,
				timestamp: new Date().toISOString(),
			};
		}

		// Handle error responses
		const errorMessage = this.extractErrorMessage(responseData);

		throw new HttpClientError(
			errorMessage,
			response.status,
			this.mapStatusToErrorCode(response.status),
			responseData,
			{ endpoint, method },
		);
	}

	private extractErrorMessage(responseData: unknown): string {
		if (typeof responseData === 'object' && responseData !== null) {
			const data = responseData as Record<string, unknown>;

			// Handle ApiErrorResponse structure: { success: false, error: { message, ... }, timestamp }
			if (data.success === false && typeof data.error === 'object' && data.error !== null) {
				const errorObj = data.error as Record<string, unknown>;
				if (typeof errorObj.message === 'string') return errorObj.message;
			}

			// Handle direct message field
			if (typeof data.message === 'string') return data.message;
			if (Array.isArray(data.message)) return data.message.join(', ');

			// Handle error as string
			if (typeof data.error === 'string') return data.error;
		}

		if (typeof responseData === 'string') {
			return responseData;
		}

		return 'Unknown error occurred';
	}

	private mapStatusToErrorCode(status: number): ErrorCode {
		if (status === 400) return 'BAD_REQUEST' as ErrorCode;
		if (status === 401) return 'UNAUTHORIZED' as ErrorCode;
		if (status === 403) return 'FORBIDDEN' as ErrorCode;
		if (status === 404) return 'NOT_FOUND' as ErrorCode;
		if (status === 409) return 'CONFLICT' as ErrorCode;
		if (status === 422) return 'VALIDATION_ERROR' as ErrorCode;
		if (status === 429) return 'RATE_LIMIT_EXCEEDED' as ErrorCode;
		if (status >= 500) return 'INTERNAL_SERVER_ERROR' as ErrorCode;
		return 'UNKNOWN_ERROR' as ErrorCode;
	}

	// ============================================================================
	// Metrics Management
	// ============================================================================

	private updateMetrics(requestMetrics: RequestMetrics): void {
		if (!this.config.enableMetrics) return;

		(this.metrics as { totalRequests: number }).totalRequests++;
		(this.metrics as { lastRequestAt: string }).lastRequestAt = new Date().toISOString();

		if (requestMetrics.success) {
			(this.metrics as { successfulRequests: number }).successfulRequests++;
		} else {
			(this.metrics as { failedRequests: number }).failedRequests++;
		}

		// Update average response time
		if (requestMetrics.duration) {
			const totalTime = this.metrics.averageResponseTime * (this.metrics.totalRequests - 1);
			(this.metrics as { averageResponseTime: number }).averageResponseTime =
				(totalTime + requestMetrics.duration) / this.metrics.totalRequests;
		}

		// Update error rate
		(this.metrics as { errorRate: number }).errorRate =
			this.metrics.failedRequests / this.metrics.totalRequests;
	}

	// ============================================================================
	// Public API
	// ============================================================================

	/**
	 * Get current metrics
	 */
	getMetrics(): ApiMetrics {
		return { ...this.metrics };
	}

	/**
	 * Abort current request
	 */
	abort(): void {
		this.abortController?.abort();
	}

	/**
	 * GET request
	 */
	async get<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, { method: 'GET', config });
	}

	/**
	 * POST request
	 */
	async post<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, { method: 'POST', data, config });
	}

	/**
	 * PUT request
	 */
	async put<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, { method: 'PUT', data, config });
	}

	/**
	 * PATCH request
	 */
	async patch<T>(
		endpoint: string,
		data?: unknown,
		config?: RequestConfig,
	): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, { method: 'PATCH', data, config });
	}

	/**
	 * DELETE request
	 */
	async delete<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, { method: 'DELETE', config });
	}
}
